/**
 * MobileEventDetail Component - Mobile-optimized event detail page
 * 
 * Comprehensive mobile event viewing experience with:
 * - Complete event information display
 * - Media support (video, audio, PDF documents)
 * - Registration integration
 * - Event image display
 * - Mobile-optimized navigation
 * - Touch-friendly interactions
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import MobileLayout from './MobileLayout';
import apiHelper from '../../utils/apiHelper';
import { addTargetBlankToLinks, cleanJson } from '../../utils/textUtils';
import './MobileEventDetail.css';

const { FILE_NAMES } = require('../../utils/constants');

function MobileEventDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [eventData, setEventData] = useState(location.state?.eventData || null);
  const [isLoading, setIsLoading] = useState(!eventData);
  const [eventImage, setEventImage] = useState(null);
  
  // Media states
  const [videoSource, setVideoSource] = useState(null);
  const [podcastSource, setPodcastSource] = useState(null);
  const [pdfSource, setPdfSource] = useState(null);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [isPodcastLoading, setIsPodcastLoading] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [isVideoEmbedded, setIsVideoEmbedded] = useState(false);
  const [isPodcastEmbedded, setIsPodcastEmbedded] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isVideoFullscreen, setIsVideoFullscreen] = useState(false);
  
  const currentBlobUrl = useRef(null);
  const videoRef = useRef(null);

  // Fetch event data if not provided via state
  useEffect(() => {
    if (!eventData && id) {
      const fetchEvent = async () => {
        try {
          const eventsResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.EVENTS);
          const found = eventsResult.fileContent.find(event => String(event.ID) === String(id));
          
          if (found) {
            // Process the event like in MobileEvents
            if (found.Topics && typeof found.Topics === 'string') {
              found.Topics = found.Topics.split(',').map(topic => topic.trim()).filter(topic => topic);
            }
            setEventData(found);
          } else {
            navigate('/mobile/events');
          }
        } catch (error) {
          console.error('Error fetching event:', error);
          navigate('/mobile/events');
        } finally {
          setIsLoading(false);
        }
      };

      fetchEvent();
    }
  }, [id, eventData, navigate]);

  // Load event image
  useEffect(() => {
    if (eventData?.ImageLink) {
      loadEventImage();
    }
  }, [eventData?.ImageLink]);

  const loadEventImage = async () => {
    if (!eventData.ImageLink) {
      setEventImage('/images/news-reading.jpg');
      return;
    }

    try {
      // Handle different image source types
      if (eventData.ImageLink.startsWith('blob:') || eventData.ImageLink.includes('https://') || 
          eventData.ImageLink.includes('assets/img/') || eventData.ImageLink.startsWith('/images/')) {
        setEventImage(eventData.ImageLink);
        return;
      }

      // Load via API
      const authToken = await apiHelper.getAuthToken();
      const imageUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/${eventData.ImageLink}`;
      
      const response = await fetch(imageUrl, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setEventImage(blobUrl);
      } else {
        setEventImage('/images/news-reading.jpg');
      }
    } catch (error) {
      setEventImage('/images/news-reading.jpg');
    }
  };

  // Media helper functions
  const getYouTubeEmbedUrl = (url) => {
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('v=')[1].split('&')[0];
      return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    } else if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1].split('?')[0];
      return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    }
    return url;
  };

  const getVimeoEmbedUrl = (url) => {
    const match = url.match(/vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/);
    if (!match) return null;
    
    const videoId = match[1];
    const privacyHash = match[2];
    
    let embedUrl = `https://player.vimeo.com/video/${videoId}?autoplay=1&title=0&byline=0&portrait=0`;
    if (privacyHash) {
      embedUrl += `&h=${privacyHash}`;
    }
    return embedUrl;
  };

  const getSpotifyEmbedUrl = (url) => {
    if (url.includes('spotify.com/')) {
      const episodeMatch = url.match(/episode\/([a-zA-Z0-9]+)/);
      const showMatch = url.match(/show\/([a-zA-Z0-9]+)/);
      
      if (episodeMatch) {
        return `https://open.spotify.com/embed/episode/${episodeMatch[1]}?utm_source=generator&theme=0`;
      } else if (showMatch) {
        return `https://open.spotify.com/embed/show/${showMatch[1]}?utm_source=generator&theme=0`;
      }
    }
    return url;
  };

  const getSoundCloudEmbedUrl = (url) => {
    if (url.includes('soundcloud.com/')) {
      return `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&auto_play=true&color=%23377e22`;
    }
    return url;
  };

  // Load video
  const loadVideo = async () => {
    if (!eventData?.VideoCollateral) return;
    
    setIsVideoLoading(true);
    const video = eventData.VideoCollateral;
    
    try {
      if (video.includes('https://')) {
        const isYouTube = video.includes('youtube.com') || video.includes('youtu.be');
        const isVimeo = video.includes('vimeo.com');

        if (isYouTube) {
          setVideoSource(getYouTubeEmbedUrl(video));
          setIsVideoEmbedded(true);
        } else if (isVimeo) {
          setVideoSource(getVimeoEmbedUrl(video));
          setIsVideoEmbedded(true);
        } else {
          setVideoSource(video);
          setIsVideoEmbedded(false);
        }
      } else {
        // Local video file: get a direct Azure Blob SAS URL and play the blob
        // directly (bytes come from Azure, not through the API).
        const baseUrl = process.env.REACT_APP_API_URL.replace('/api/', '/');
        const sasEndpoint = `${baseUrl}media/sas/${encodeURIComponent(video)}`;

        try {
          const resp = await fetch(sasEndpoint);
          const data = resp.ok ? await resp.json() : null;

          if (data && data.url) {
            setVideoSource(data.url);
            setIsVideoEmbedded(false);
          } else {
            const { fileContent } = await apiHelper.fetchVideoByName(video);
            const newBlobUrl = URL.createObjectURL(fileContent);
            currentBlobUrl.current = newBlobUrl;
            setVideoSource(newBlobUrl);
            setIsVideoEmbedded(false);
          }
        } catch (error) {
          const { fileContent } = await apiHelper.fetchVideoByName(video);
          const newBlobUrl = URL.createObjectURL(fileContent);
          currentBlobUrl.current = newBlobUrl;
          setVideoSource(newBlobUrl);
          setIsVideoEmbedded(false);
        }
      }
    } catch (error) {
      console.error('Error loading video:', error);
    } finally {
      setIsVideoLoading(false);
    }
  };

  // Load podcast/audio
  const loadPodcast = async () => {
    if (!eventData?.AudioCollateral) return;
    
    setIsPodcastLoading(true);
    const audio = eventData.AudioCollateral;
    
    try {
      if (audio.includes('https://')) {
        const isSpotify = audio.includes('spotify.com');
        const isSoundCloud = audio.includes('soundcloud.com');
        
        if (isSpotify) {
          setPodcastSource(getSpotifyEmbedUrl(audio));
          setIsPodcastEmbedded(true);
        } else if (isSoundCloud) {
          setPodcastSource(getSoundCloudEmbedUrl(audio));
          setIsPodcastEmbedded(true);
        } else {
          setPodcastSource(audio);
          setIsPodcastEmbedded(false);
        }
      } else {
        // Local audio file
        const baseUrl = process.env.REACT_APP_API_URL.replace('/api/', '/');
        const streamingUrl = `${baseUrl}media/stream/${audio}`;
        
        try {
          // No Range header: behind IIS a HEAD carrying a Range returns 416,
          // which made this check "fail" and fall back to a full download.
          const testResponse = await fetch(streamingUrl, {
            method: 'HEAD'
          });

          if (testResponse.ok || testResponse.status === 206) {
            setPodcastSource(streamingUrl);
            setIsPodcastEmbedded(false);
          } else {
            const { fileContent } = await apiHelper.fetchAudioByName(audio);
            const newBlobUrl = URL.createObjectURL(fileContent);
            setPodcastSource(newBlobUrl);
            setIsPodcastEmbedded(false);
          }
        } catch (error) {
          const { fileContent } = await apiHelper.fetchAudioByName(audio);
          const newBlobUrl = URL.createObjectURL(fileContent);
          setPodcastSource(newBlobUrl);
          setIsPodcastEmbedded(false);
        }
      }
    } catch (error) {
      console.error('Error loading audio:', error);
    } finally {
      setIsPodcastLoading(false);
    }
  };

  // Load PDF
  const loadPdf = async () => {
    if (!eventData?.FileCollateral) return;
    
    setIsPdfLoading(true);
    
    try {
      if (eventData.FileCollateral.includes('https://')) {
        setPdfSource(eventData.FileCollateral);
      } else {
        const authToken = await apiHelper.getAuthToken();
        const pdfUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/${eventData.FileCollateral}`;
        setPdfSource(`${pdfUrl}?token=${authToken}`);
      }
    } catch (error) {
      console.error('Error loading PDF:', error);
    } finally {
      setIsPdfLoading(false);
    }
  };

  // Format functions
  const formatEventDate = (date) => {
    if (!date) return 'Date TBD';
    try {
      const eventDate = new Date(date);
      if (isNaN(eventDate.getTime())) return 'Date TBD';
      
      return eventDate.toLocaleDateString('en-GB', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Date TBD';
    }
  };

  const formatEventTime = (startTime, endTime) => {
    if (!startTime && !endTime) return null;
    if (startTime === 'TBC' || endTime === 'TBC') return 'TBC';
    
    try {
      let timeStr = '';
      if (startTime) {
        const time = new Date(`1970-01-01T${startTime}`);
        if (!isNaN(time.getTime())) {
          timeStr += time.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
        }
      }
      if (endTime && startTime) {
        const time = new Date(`1970-01-01T${endTime}`);
        if (!isNaN(time.getTime())) {
          timeStr += ' - ' + time.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
        }
      }
      return timeStr || null;
    } catch (error) {
      return null;
    }
  };

  // Video control functions
  const handleVideoPlay = () => setIsVideoPlaying(true);
  const handleVideoPause = () => setIsVideoPlaying(false);

  const handleVideoFullscreen = () => {
    if (videoRef.current) {
      setIsVideoFullscreen(true);
    }
  };

  const handleVideoExitFullscreen = () => {
    setIsVideoFullscreen(false);
  };

  // Escape key handler for fullscreen video
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isVideoFullscreen) {
        handleVideoExitFullscreen();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isVideoFullscreen]);

  // Cleanup blob URLs
  useEffect(() => {
    return () => {
      if (eventImage && eventImage.startsWith('blob:')) {
        URL.revokeObjectURL(eventImage);
      }
      if (currentBlobUrl.current) {
        URL.revokeObjectURL(currentBlobUrl.current);
      }
      if (podcastSource && podcastSource.startsWith('blob:')) {
        URL.revokeObjectURL(podcastSource);
      }
    };
  }, [eventImage, podcastSource]);

  if (isLoading) {
    return (
      <MobileLayout title="Loading..." showBackButton={true} onBackClick={() => navigate('/mobile/events')}>
        <div className="mobile-loading">
          <div className="mobile-loading-spinner">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
          <div className="mobile-loading-text">Loading event details...</div>
        </div>
      </MobileLayout>
    );
  }

  if (!eventData) {
    return (
      <MobileLayout title="Event Not Found" showBackButton={true} onBackClick={() => navigate('/mobile/events')}>
        <div className="mobile-error">
          <div className="mobile-error-icon">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <div className="mobile-error-text">Event not found</div>
          <button 
            className="mobile-error-btn"
            onClick={() => navigate('/mobile/events')}
          >
            Back to Events
          </button>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout title={eventData.Title} showBackButton={true} onBackClick={() => navigate('/mobile/events')}>
      <div className="mobile-event-detail">
        {/* Event Header */}
        <div className="mobile-event-header">
          {eventImage && (
            <div className="mobile-event-header-image">
              <img src={eventImage} alt={eventData.Title} />
            </div>
          )}
          
          <div className="mobile-event-header-content">
            <div className="mobile-event-badges">
              {eventData.InPerson_x002f_Virtual && (
                <span className="mobile-event-type-badge">
                  {eventData.InPerson_x002f_Virtual}
                </span>
              )}
              {eventData.EventCategory && (
                <span className="mobile-event-category-badge">
                  {eventData.EventCategory}
                </span>
              )}
            </div>
            
            <h1 className="mobile-event-header-title">{eventData.Title}</h1>
            
            <div className="mobile-event-meta">
              <div className="mobile-event-meta-item">
                <i className="fas fa-calendar"></i>
                <span>{formatEventDate(eventData.EventDate)}</span>
              </div>
              
              {formatEventTime(eventData.StartTime, eventData.EndTime) && (
                <div className="mobile-event-meta-item">
                  <i className="fas fa-clock"></i>
                  <span>{formatEventTime(eventData.StartTime, eventData.EndTime)}</span>
                </div>
              )}
              
              {eventData.EventLocation && (
                <div className="mobile-event-meta-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>{eventData.EventLocation}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Registration/Agenda Actions */}
        {(eventData.RegistrationLink || eventData.AgendaLink) && (
          <div className="mobile-event-actions">
            {eventData.RegistrationLink && (
              <button 
                className="mobile-event-action-btn mobile-event-register"
                onClick={() => window.open(eventData.RegistrationLink, '_blank')}
              >
                <i className="fas fa-user-plus"></i>
                Register for Event
              </button>
            )}
            
            {eventData.AgendaLink && (
              <button 
                className="mobile-event-action-btn mobile-event-agenda"
                onClick={() => window.open(eventData.AgendaLink, '_blank')}
              >
                <i className="fas fa-list-alt"></i>
                View Agenda
              </button>
            )}
          </div>
        )}

        {/* Event Description */}
        {eventData.EventDescription_x002f_Narrative && (
          <div className="mobile-event-section">
            <h2 className="mobile-event-section-title">
              <i className="fas fa-info-circle"></i>
              About This Event
            </h2>
            <div 
              className="mobile-event-description"
              dangerouslySetInnerHTML={{ 
                __html: addTargetBlankToLinks(cleanJson(eventData.EventDescription_x002f_Narrative)) 
              }}
            />
          </div>
        )}

        {/* Media Sections */}
        {/* Video Section */}
        {eventData.VideoCollateral && (
          <div className="mobile-event-section">
            <h2 className="mobile-event-section-title">
              <i className="fas fa-video"></i>
              Event Video
            </h2>
            {!videoSource ? (
              <button className="mobile-media-load-btn" onClick={loadVideo} disabled={isVideoLoading}>
                {isVideoLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Loading video...
                  </>
                ) : (
                  <>
                    <i className="fas fa-play"></i>
                    Load Video
                  </>
                )}
              </button>
            ) : (
              <div className={`mobile-video-container ${isVideoFullscreen ? 'fullscreen' : ''}`}>
                {isVideoEmbedded ? (
                  <iframe
                    src={videoSource}
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={eventData.Title}
                  />
                ) : (
                  <div className="mobile-video-wrapper">
                    <video
                      ref={videoRef}
                      width="100%"
                      height="100%"
                      controls
                      src={videoSource}
                      onPlay={handleVideoPlay}
                      onPause={handleVideoPause}
                      onEnded={handleVideoPause}
                    />
                    <div className="mobile-video-controls">
                      <button
                        className="mobile-video-expand-btn"
                        onClick={handleVideoFullscreen}
                        title="Expand video"
                      >
                        <i className="fas fa-expand"></i>
                      </button>
                    </div>
                  </div>
                )}
                
                {isVideoFullscreen && !isVideoEmbedded && (
                  <div className="mobile-video-fullscreen-controls">
                    <button
                      className="mobile-video-close-btn"
                      onClick={handleVideoExitFullscreen}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Audio/Podcast Section */}
        {eventData.AudioCollateral && (
          <div className="mobile-event-section">
            <h2 className="mobile-event-section-title">
              <i className="fas fa-microphone"></i>
              Event Audio
            </h2>
            {!podcastSource ? (
              <button className="mobile-media-load-btn" onClick={loadPodcast} disabled={isPodcastLoading}>
                {isPodcastLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Loading audio...
                  </>
                ) : (
                  <>
                    <i className="fas fa-headphones"></i>
                    Load Audio
                  </>
                )}
              </button>
            ) : (
              <div className="mobile-podcast-container">
                {isPodcastEmbedded ? (
                  <iframe
                    src={podcastSource}
                    width="100%"
                    height="352"
                    frameBorder="0"
                    allowFullScreen
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy"
                    title={eventData.Title}
                  />
                ) : (
                  <audio
                    width="100%"
                    controls
                    src={podcastSource}
                    style={{ width: '100%', maxWidth: '100%' }}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {/* PDF Section */}
        {eventData.FileCollateral && (
          <div className="mobile-event-section">
            <h2 className="mobile-event-section-title">
              <i className="fas fa-file-pdf"></i>
              Event Documents
            </h2>
            {!pdfSource ? (
              <button className="mobile-media-load-btn" onClick={loadPdf} disabled={isPdfLoading}>
                {isPdfLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Loading document...
                  </>
                ) : (
                  <>
                    <i className="fas fa-file-pdf"></i>
                    Load Document
                  </>
                )}
              </button>
            ) : (
              <div className="mobile-pdf-container">
                <iframe
                  src={pdfSource}
                  width="100%"
                  height="500"
                  title={`${eventData.Title} - Document`}
                  style={{ border: 'none', borderRadius: '8px' }}
                />
                <div className="mobile-pdf-message">
                  {isPdfLoading ? 'PDF is loading...' : 'Document loaded successfully'}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Additional Event Information */}
        {/* Agenda */}
        {eventData.Agenda && (
          <div className="mobile-event-section">
            <h2 className="mobile-event-section-title">
              <i className="fas fa-list"></i>
              Agenda
            </h2>
            <div 
              className="mobile-event-content"
              dangerouslySetInnerHTML={{ 
                __html: addTargetBlankToLinks(cleanJson(eventData.Agenda)) 
              }}
            />
          </div>
        )}

        {/* Speakers */}
        {eventData.Speakers && (
          <div className="mobile-event-section">
            <h2 className="mobile-event-section-title">
              <i className="fas fa-microphone"></i>
              Speakers
            </h2>
            <div className="mobile-event-speakers">
              {eventData.Speakers.split('\n').filter(speaker => speaker.trim()).map((speaker, index) => (
                <div key={index} className="mobile-event-speaker">
                  <i className="fas fa-user-tie"></i>
                  <span>{speaker.trim()}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Event Sponsors */}
        {eventData.EventSponsors && (() => {
          const cleanedSponsors = eventData.EventSponsors
            .replace(/<[^>]*>/g, '')
            .replace(/&nbsp;/g, ' ')
            .trim();
          
          return cleanedSponsors.length > 0;
        })() && (
          <div className="mobile-event-section">
            <h2 className="mobile-event-section-title">
              <i className="fas fa-handshake"></i>
              Event Sponsors
            </h2>
            <div 
              className="mobile-event-content"
              dangerouslySetInnerHTML={{ 
                __html: addTargetBlankToLinks(cleanJson(eventData.EventSponsors)) 
              }}
            />
          </div>
        )}

        {/* Topics */}
        {eventData.Topics && eventData.Topics.length > 0 && (
          <div className="mobile-event-section">
            <h2 className="mobile-event-section-title">
              <i className="fas fa-tags"></i>
              Topics
            </h2>
            <div className="mobile-event-topics">
              {eventData.Topics.map((topic, index) => (
                <span key={index} className="mobile-event-topic-tag">
                  {topic}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </MobileLayout>
  );
}

export default MobileEventDetail; 